<script setup lang='ts'>
import { Vue3Lottie } from 'vue3-lottie';
import WisionLogo from '../WisionLogo.vue';
import { useRouter, useRoute } from 'vue-router';
import { useAppState } from '../../store/state';
import EmailData from '../../assets/lottie/email_sent.json';
import CreateAccountData from '../../assets/lottie/create_account.json';
import ActivatingUnitData from '../../assets/lottie/activating_unit.json';
import ErrorData from '../../assets/lottie/error.json';
import { ref, onMounted, onUnmounted } from 'vue';
import { event } from 'vue-gtag';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const POLL_STATUS_INTERVAL = 10000;

const router = useRouter();
const route = useRoute();
const state = useAppState();
const tagError = ref(false);

const creatingNewUser = !state.hasAccess;
const currentStep = ref(parseInt(localStorage.getItem('currentStep') ?? '0'));

const stepps = [
  {
    lottieData: CreateAccountData,
    text: `${t('nfc.activating.creatingAccount')}...`
  },
  {
    lottieData: EmailData,
    text: `${t('nfc.activating.sendingLink')}...`
  },
  {
    lottieData: ActivatingUnitData,
    text: `${t('nfc.activating.activatingDevice')}...`
  }
];

const handleNextStep = () => {
  localStorage.setItem('currentStep', JSON.stringify(currentStep.value + 1));
  currentStep.value = currentStep.value + 1;
};

let pollIntervalId: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  pollIntervalId = setInterval(async () => {
    const result = await state.fetchTag(route.params.uuid as string);
    if (result?.tag?.status === 'error') {
      tagError.value = true;
      return;
    }
    if (result?.tag?.status === 'ready' && (!creatingNewUser || currentStep.value === stepps.length - 1)) {
      localStorage.removeItem('currentStep');
      event('unit_activated', { method: 'Google' });
      router.push({ name: 'view', params: { uuid: route.params.uuid } });
    }
  }, POLL_STATUS_INTERVAL);
});

onUnmounted(() => {
  if (pollIntervalId !== null) {
    clearInterval(pollIntervalId);
    pollIntervalId = null;
  }
});
</script>

<template>
  <div class="flex flex-col h-full items-center w-full px-10 text-onboard-shade2 justify-center">
    <div class="flex flex-col items-center">
      <WisionLogo :width="114" class="text-onboard-foreground"/>
      <span class="text-xl mt-6 uppercase">
        {{ $t('nfc.activating.title') }}
      </span>
      <div v-if="tagError" class="pt-5">
        <Vue3Lottie
          :animationData="ErrorData"
          :height="100"
          :width="100"
          :loop="true"
        />
        <span class="animate-pulse">
          {{ `${t('nfc.activating.error')}...` }}
        </span>
      </div>
      <div v-if="creatingNewUser && !tagError" class="text-center color-white pt-5 w-full">
        <div v-for="step, index in stepps" :key="step.text" class="relative">
          <div class="h-[100px] w-full absolute top-0">
            <Vue3Lottie
              v-if="currentStep === index"
              :animationData="step.lottieData"
              :height="100"
              :width="100"
              :loop="currentStep === stepps.length - 1"
              :onOnComplete="() => currentStep < stepps.length - 1 && handleNextStep()"
            />
          </div>
          <span class="absolute left-0 right-0 w-full top-28" :class="currentStep === index ? 'show' : 'hide'">
            {{ step.text }}
          </span>
        </div>
      </div>
      <div>
        <div class="pt-5" v-if="!creatingNewUser && !tagError">
          <Vue3Lottie
            :animationData="ActivatingUnitData"
            :height="100"
            :width="100"
            :loop="true"
          />
          <span class="animate-pulse">
            {{ `${t('nfc.activating.activatingDevice')}...` }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
  .hide {
    transform: translate(0, 200%);
    opacity: 0;
    transition: all 0.7s;
  }

  .show {
    opacity: 1;
    transition: all 1s;

  }
</style>
